import type { LeadVariant } from './types';

/** Weighted-random bucketing, sticky per form via localStorage so a visitor keeps seeing the same
 * variant across page loads — the server only records whichever variantId the SDK reports, it
 * never assigns one itself (see LeadSubmissionService / PublicLeadFormController#impression). */
export function pickVariant(formId: string, variants: LeadVariant[]): LeadVariant | null {
  if (variants.length === 0) return null;

  const key = `cms_lf_variant_${formId}`;
  const storedId = safeGet(key);
  const stored = storedId ? variants.find((v) => v.id === storedId) : undefined;
  if (stored) return stored;

  const chosen = weightedRandom(variants);
  if (chosen) safeSet(key, chosen.id);
  return chosen;
}

function weightedRandom(variants: LeadVariant[]): LeadVariant | null {
  const totalWeight = variants.reduce((sum, v) => sum + Math.max(v.weight, 0), 0);
  if (totalWeight <= 0) return variants[0] ?? null;

  let roll = Math.random() * totalWeight;
  for (const variant of variants) {
    roll -= Math.max(variant.weight, 0);
    if (roll <= 0) return variant;
  }
  return variants[variants.length - 1];
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Bucketing just becomes non-sticky (re-rolled each page load) if storage is unavailable.
  }
}
